<template>
  <DataTable
    v-if="data && data.length"
    v-model:filters="filters"
    :value="data"
    :rows="rowsPerPage"
    :paginator="paginator"
    :rows-per-page-options="paginationOptions"
    removableSort
    filterDisplay="menu"
    @page="onPageChange"
    scrollable
    scrollHeight="100%"
    maximizable
    v-model:expandedRows="expandedRows"
    :rowExpansion="masterDetail"
    resizableColumns
    :columnResizeMode="columnResizeMode"
    :showGridlines="showGridLines"
  >
    <template #header>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <Button
          v-if="hasHeaderFilter"
          type="button"
          icon="pi pi-filter-slash"
          :label="$t('dataGrid.clearFilters')"
          outlined
          @click="clearFilters"
        />
        <slot name="header" />
      </div>
    </template>

    <Column v-if="masterDetail" expander style="width: 3em"></Column>

    <Column v-if="hasActionsSlot" :header="$t('dataGrid.actions')">
      <template #body="slotProps">
        <slot name="custom-actions" :data="slotProps.data" />
      </template>
    </Column>

    <Column
      v-for="column in columns"
      :key="column.field"
      :field="column.field"
      :header="column.header"
      :sortable="column.sort"
      :filterField="column.field"
      :dataType="column.type"
      :frozen="column.lock || false"
      style="max-width: 200px"
    >
      <template #body="slotProps">
        <slot
          v-if="hasCustomSlot(column)"
          :name="getCustomSlotName(column)"
          :data="slotProps.data"
        />
        <span v-else>{{ slotProps.data[column.field] }}</span>
      </template>

      <template
        v-if="column.headerFilter"
        #filter="{ filterModel, filterCallback }"
      >
        <template v-if="column.type === 'date'">
          <DatePicker
            v-model="filterModel.value"
            dateFormat="mm/dd/yy"
            @change="filterCallback()"
            placeholder="Filter by date"
          />
        </template>
        <template v-else-if="column.type === 'text'">
          <InputText v-model="filterModel.value" @input="filterCallback()" />
        </template>
        <template v-else-if="column.type === 'numeric'">
          <InputNumber
            v-model="filterModel.value"
            @input="filterCallback()"
            placeholder="Filter by number"
          />
        </template>
      </template>
    </Column>

    <template #expansion="{ data }">
      <slot name="expansion" :data="data" />
    </template>

    <!-- Conditionally render footer only if there's content in the slot -->
    <template v-if="hasFooterContent" #footer>
      <slot name="footer" />
    </template>
  </DataTable>

  <div v-else class="no-data-container">
    <div class="no-data-message">
      <h3>{{ $t("dataGrid.noData.title") }}</h3>
      <p>{{ $t("dataGrid.noData.description") }}</p>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import DatePicker from "primevue/calendar";
import { FilterMatchMode, FilterOperator } from "@primevue/core/api";
import InputNumber from "primevue/inputnumber";

export default defineComponent({
  name: "DataGrid",
  props: {
    data: {
      type: Array,
      required: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    paginationOptions: {
      type: Array,
      default: () => [5, 10, 20, 50],
    },
    defaultRowsPerPage: {
      type: Number,
      default: 5,
    },
    masterDetail: {
      type: Boolean,
      default: false,
    },
    paginator: {
      type: Boolean,
      default: true,
    },
    showGridLines: {
      type: Boolean,
      default: false,
    },
    columnResizeMode: {
      type: String,
      default: "expand",
    },
  },
  components: {
    DataTable,
    Column,
    InputText,
    DatePicker,
    InputNumber,
  },
  setup(props, { slots }) {
    const first = ref(0);
    const rowsPerPage = ref(props.defaultRowsPerPage);
    const filters = ref({});
    const expandedRows = ref(null);

    const initializeFilters = () => {
      props.columns.forEach((column) => {
        if (column.headerFilter) {
          if (column.type === "text") {
            filters.value[column.field] = {
              operator: FilterOperator.AND,
              constraints: [
                { value: null, matchMode: FilterMatchMode.STARTS_WITH },
              ],
            };
          } else if (column.type === "date") {
            filters.value[column.field] = {
              operator: FilterOperator.AND,
              constraints: [
                { value: null, matchMode: FilterMatchMode.DATE_IS },
              ],
            };
          } else if (column.type === "numeric") {
            filters.value[column.field] = {
              operator: FilterOperator.AND,
              constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
            };
          }
        }
      });
    };

    initializeFilters();

    const hasActionsSlot = computed(() => !!slots["custom-actions"]);

    const onPageChange = (event) => {
      first.value = event.first;
      rowsPerPage.value = event.rows;
    };

    const hasCustomSlot = (column) => {
      const slotName = `custom-template-${column.field}`;
      return !!slots[slotName];
    };

    const getCustomSlotName = (column) => {
      return `custom-template-${column.field}`;
    };

    const hasHeaderFilter = computed(() =>
      props.columns.some((column) => column.headerFilter)
    );

    const clearFilters = () => {
      initializeFilters();
    };

    // Check if the footer slot has content
    const hasFooterContent = computed(() => !!slots.footer);

    return {
      first,
      rowsPerPage,
      onPageChange,
      hasActionsSlot,
      hasCustomSlot,
      getCustomSlotName,
      filters,
      expandedRows,
      clearFilters,
      hasHeaderFilter,
      hasFooterContent,
    };
  },
});
</script>

<style>
.p-datatable-table-container {
  min-height: 300px;
}

.no-data-container {
  min-height: 300px;
}
</style>
